import React, { useEffect, useState, useContext, SyntheticEvent } from "react";
import { FormattedMessage } from "react-intl-phraseapp";
import { Alert, Caret, Dropdown } from "@cmsgov/ds-medicare-gov";
import { useTranslate, sendTealiumEvent, IntlContext } from "../../../helpers";
import { AppPage, PrintButton, LoadingMask } from "../../../components";
import {
  MedigapPlanInfo,
  MedigapPlanPremiumRange,
  getMedigapPlansForState,
} from "../utils/plans";
import { getMedigapPlanPremiumRanges } from "../../../api";
import MedigapPlanCard from "./MedigapPlanCard";
import routes from "../../../app/routes";
import states from "../../EnrollmentFormPage/states";
import { AppContext } from "../../../app/store";
import GetMoreAccuratePrice from "./GetMoreAccuratePrice";
import MedigapChangeLocation from "./MedigapChangeLocation";
import { sortBy } from "sort-by-typescript";
import { linkToMedigapStateInsuranceDepartments } from "../../../helpers/CONSTANTS";

enum MedigapPlansSortOption {
  ALPHABETICALLY = "alphabetically",
  MONTHLY_PREMIUM = "monthly_premium",
}

const MedigapPlans: React.FunctionComponent = () => {
  const t = useTranslate();
  const fm = useContext(IntlContext).formatMessage;
  const [plansAndRanges, setPlansAndRanges] = useState<
    MedigapPlanInfo[] | undefined
  >(undefined);
  const [sortedPlans, setSortedPlans] = useState<MedigapPlanInfo[]>([]);
  const [sortByOption, setSortByOption] = useState<MedigapPlansSortOption>(
    MedigapPlansSortOption.ALPHABETICALLY
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | JSX.Element>("");
  const {
    state: { county, zipcode = "", language, demographicInfo },
  } = useContext(AppContext);

  const stateAbbrev = county?.state || "";
  const stateName = states.find(s => {
    return s.abbreviation === stateAbbrev;
  })?.name;

  const fetchPlanPremiumRanges = async (): Promise<void> => {
    try {
      const premiumRanges = await getMedigapPlanPremiumRanges(
        stateAbbrev,
        zipcode,
        demographicInfo
      );
      // The endpoint returns more plans than we care about, so this reducer
      // creates a list of only the premium ranges we get from the endpoint that
      // match a plan we have data for
      setPlansAndRanges(
        premiumRanges.reduce(
          (acc: MedigapPlanInfo[], premiumRange: MedigapPlanPremiumRange) => {
            const plans = getMedigapPlansForState(stateAbbrev);
            const plan = plans.find(
              p => p.plan_type === premiumRange.medigap_plan_type
            );

            if (plan) {
              acc.push({
                plan,
                premiumRange,
              });
            }

            return acc;
          },
          []
        )
      );
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setError(t("medigap.api_error"));
    }
  };

  const medigapPlansHeaderId = "medigap-plans-results-header";

  useEffect(() => {
    // FormattedMessage does not accept an HTML ID, but we need one for the plan results <ol>
    document.querySelector("h1")?.setAttribute("id", medigapPlansHeaderId);

    sendTealiumEvent(
      "Step 1: Decide what plans you want",
      `Results UI: Sort Plans: ${sortByOption}`
    );
  }, []);

  useEffect(() => {
    if (plansAndRanges) {
      // We need to translate the plan names before sorting
      const sorted = [...plansAndRanges].map(x => {
        return {
          plan: { ...x.plan, plan_name: t(x.plan.plan_name) },
          premiumRange: x.premiumRange,
        };
      });

      if (sortByOption === MedigapPlansSortOption.ALPHABETICALLY) {
        sorted.sort(sortBy("plan.plan_name"));
      } else if (sortByOption === MedigapPlansSortOption.MONTHLY_PREMIUM) {
        sorted.sort(
          sortBy(
            "premiumRange.monthly_rate_min",
            "plan.plan_name",
            (k: string, v: number) => Math.round(v)
          )
        );
      }

      if (!sorted.length) {
        setError(
          <FormattedMessage
            id="medigap.plans.no_plans"
            values={{
              stateDepartmentOfInsurance: (
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={linkToMedigapStateInsuranceDepartments(language)}
                >
                  {t("medigap.plans.state_dept_ins")}
                </a>
              ),
            }}
          />
        );
      } else {
        setError("");
      }

      setSortedPlans(sorted);
    }
  }, [plansAndRanges, sortByOption, language]);

  useEffect(() => {
    if (zipcode && county) {
      setLoading(true);
      fetchPlanPremiumRanges();
    }
  }, [zipcode, county, demographicInfo]);

  return (
    <AppPage
      title={fm(
        {
          id: "medigap.plans.heading",
        },
        { state: stateName }
      )}
      hideLogin
    >
      <LoadingMask loading={loading} delay={500} />
      <div className="ds-u-margin-top--2 ds-l-row ds-u-justify-content--between mct-hide-print">
        <a
          href={`#${routes.medigap.landingPage}?lang=${language}`}
          onClick={(): void => {
            sendTealiumEvent(
              "Step 1: Decide what plans you want",
              "Back to find a Medigap policy"
            );
          }}
          className="ds-c-button ds-c-button--transparent PlanDetails__backButton"
        >
          <Caret left className="ds-u-valign--middle ds-u-margin-right--1" />
          {t("medigap.back_to_landing_page")}
        </a>

        <PrintButton
          tealiumProps={{
            event_action: "Step 1: Decide what plans you want",
            event_label: "Print",
          }}
        />
      </div>
      <div className="MedigapPlans">
        <div className="ds-u-font-weight--semibold">{t("medigap.step_1")}</div>
        <FormattedMessage
          id="medigap.plans.heading"
          tagName="h1"
          values={{ state: stateName }}
        />
        <MedigapChangeLocation />
        {!!sortedPlans.length && <GetMoreAccuratePrice />}
        {error && <Alert variation="error">{error}</Alert>}
        {!!sortedPlans.length && !loading && (
          <>
            <div className="ds-u-md-display--flex ds-u-justify-content--between ds-u-align-items--center ds-u-margin-y--4">
              <FormattedMessage
                id="medigap.num_plans_in_state"
                values={{ count: sortedPlans.length }}
              />
              <Dropdown
                label={t("medigap.plan_results.sort_by")}
                name="sort-plans-by"
                className="ds-u-display--flex ds-u-align-items--center ds-u-margin-top--2 ds-u-md-margin-top--0"
                labelClassName="MedigapPlans__sortSelectLabel"
                value={sortByOption}
                onChange={(e: SyntheticEvent<HTMLSelectElement>): void => {
                  sendTealiumEvent(
                    "Step 1: Decide what plans you want",
                    `Results UI: Sort Plans: ${e.currentTarget.value}`
                  );
                  setSortByOption(
                    e.currentTarget.value as MedigapPlansSortOption
                  );
                }}
                options={[
                  {
                    label: t("medigap.plan_results.plan_name_sort"),
                    value: MedigapPlansSortOption.ALPHABETICALLY,
                  },
                  {
                    label: t(
                      "medigap.plan_results.monthly_premium_low_high_sort"
                    ),
                    value: MedigapPlansSortOption.MONTHLY_PREMIUM,
                  },
                ]}
              />
            </div>
            <ol
              className="ds-c-list--bare medigap-plans"
              aria-labelledby={medigapPlansHeaderId}
            >
              {sortedPlans.map(planInfo => {
                return (
                  <li key={planInfo.plan.plan_type}>
                    <MedigapPlanCard
                      plan={planInfo.plan}
                      premiumRange={planInfo.premiumRange}
                    />
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </div>
    </AppPage>
  );
};

export default MedigapPlans;
